// Components lib
// Libs
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import deleteTypeItem from '@/common/api/type-items/deleteTypeItem';
import Snack from '@/common/components/molecules/Snack/Snack';
// Constants
import { ITEM_TYPES } from '@/common/constants';

import { useDefaultCartContext } from './DefaultCartContext';

// Context creation
const RemoveFromContext = createContext(null);

export const RemoveFromContextProvider = ({ children, isLoggedIn }) => {
  // TODO: used in cart page to refresh cash, not yet developed
  // eslint-disable-next-line no-unused-vars
  const queryClient = useQueryClient();
  // Get default cart context t get the refetchDefaultCart callback to refresh quantity in header after action on cart item
  const { refetchDefaultCart } = useDefaultCartContext();
  // Translator
  const { t } = useTranslation();
  /**
   * Trigger refresh type after action (add, cancel ...)
   * @type {boolean}
   */
  // CAUTION: ******************************************
  // this attribute is set to false because of multiple call to AS400
  // so refreshCart Methode is not use at this moment.
  const triggerRefreshCart = false;
  // TODO: set to true or false depends on AS400 interaction
  // eslint-disable-next-line no-unused-vars
  const triggerRefreshQuote = false;
  // TODO: set to true or false depends on AS400 interaction
  // eslint-disable-next-line no-unused-vars
  const triggerRefreshWishlist = false;
  /**
   * Remove item form target
   * Get mutator and set the mutation api
   */
  const {
    mutate: mutateRemoveItems,
    isLoading: isLoadingRemoveItems,
    isSuccess: isSuccessRemoveItems,
  } = useMutation(deleteTypeItem);

  /**
   * Callback given to the children component to remove item form a specified type, if exist and if user is logged
   * @param {object} target
   * @param {object} item to remove from target
   * @param {string} type of target (cart|quotes|wishlists)
   * @param {function} onSuccessCallback callback executed after success
   */
  const removeFrom = useCallback(
    ({ type, item, target, onSuccessCallback }) => {
      if (isLoggedIn) {
        mutateRemoveItems(
          {
            target,
            item,
            type,
          },
          {
            onSuccess: ({ headers }) => {
              // Refresh header to display the new quantity
              if (type === ITEM_TYPES.carts) {
                // TODO:  depends on type
                refetchDefaultCart();
              }
              // TODO: Refresh cart cache to get item instantly in cart page if triggerRefreshCart
              if (triggerRefreshCart) {
                //refreshCart();
              }
              // Inform the user that an item have been removed from the target name with a flashMessage.
              toast.success(
                <Snack
                  message={t('common:messages:forms.success.removed', {
                    name: target.name,
                  })}
                />,
              );
              // execute onSuccessCallback callback given
              // Installer quote need update totals extracted from http response headers
              onSuccessCallback(type === ITEM_TYPES.installer && { headers });
            },
            onError: (error) => {
              // In case of error
              if (error.response.status === 500) {
                toast.error(
                  <Snack
                    message={t('common:messages:forms.errors.server_error')}
                  />,
                );
              } else {
                toast.error(
                  <Snack message={t('common:messages:forms.errors.default')} />,
                );
              }
            },
          },
        );
      } else {
        toast.error(
          <Snack message={t('common:messages:forms.errors.not_logged')} />,
        );
      }
    },
    [isLoggedIn, mutateRemoveItems, refetchDefaultCart, t, triggerRefreshCart],
  );

  /**
   * Remove item from the given cart
   * This method calls removeFrom with the right signature
   * @param {object} cart {id: int, name: string}
   * @param {object} item to add to targeted cart
   * @param {function} onSuccessCallback callback on success
   */
  const removeFromCart = useCallback(
    ({ cart, item, onSuccessCallback = () => {} }) =>
      removeFrom({
        target: { ...cart },
        item,
        type: ITEM_TYPES.carts,
        onSuccessCallback,
      }),
    [removeFrom],
  );

  /**
   * Remove item from the given quote
   * This method calls removeFrom with the right signature
   * @param {object} quote {id: int, name: string}
   * @param {object} item to add to targeted quote
   * @param {function} onSuccessCallback callback on success
   */
  const removeFromQuote = useCallback(
    ({ quote, item, onSuccessCallback = () => {} }) =>
      removeFrom({
        target: { ...quote },
        item,
        type: ITEM_TYPES.quotes,
        onSuccessCallback,
      }),
    [removeFrom],
  );

  /**
   * Remove item from the given wishlists
   * This method calls removeFrom with the right signature
   * @param {object} wishlist {id: int, name: string}
   * @param {object} item to add to targeted wishlist
   * @param {function} onSuccessCallback callback on success
   */
  const removeFromWishlist = useCallback(
    ({ wishlist, item, onSuccessCallback = () => {} }) =>
      removeFrom({
        target: { ...wishlist },
        item,
        type: ITEM_TYPES.wishlists,
        onSuccessCallback,
      }),
    [removeFrom],
  );

  return (
    <RemoveFromContext.Provider
      value={{
        removeFromCart,
        removeFromQuote,
        removeFromWishlist,
        isLoading: isLoadingRemoveItems,
        isSuccess: isSuccessRemoveItems,
      }}
    >
      {children}
    </RemoveFromContext.Provider>
  );
};

RemoveFromContextProvider.defaultProps = {
  isLoggedIn: false,
};

RemoveFromContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  isLoggedIn: PropTypes.bool,
};

export const useRemoveFromContext = () => useContext(RemoveFromContext);
